/**
 * Auth + profile API surface. ``authApi`` handles the Google OAuth round-trip
 * and session lifecycle; ``usersApi`` carries the user-managed profile patch.
 *
 * Backed by ``backend/auth/router.py`` and ``backend/users/router.py``.
 */
import { env } from "@/env";
import apiClient from "@lib/axios";
import type { AuthUser } from "@models/quota";
import { AuthUserSchema } from "@services/schemas";

export const authApi = {
  getMe: async (): Promise<AuthUser> => {
    const { data } = await apiClient.get("/auth/me");
    return AuthUserSchema.parse(data);
  },
  logout: async (): Promise<void> => {
    await apiClient.post("/auth/logout");
  },
  getLoginUrl: (): string => {
    return `${env.VITE_API_BASE_URL}/auth/google/login`;
  },
};

// ── Profile ─────────────────────────────────────────────────────────────────

export interface UpdateProfileRequest {
  /** Null or empty clears the override and reverts to the Google name. */
  name_override: string | null;
}

export const usersApi = {
  /** PATCH /users/me — update the user-managed profile fields. Returns
   *  the refreshed AuthUser shape so the caller can swap state without
   *  a follow-up GET. */
  updateProfile: async (body: UpdateProfileRequest): Promise<AuthUser> => {
    const { data } = await apiClient.patch("/users/me", body);
    return AuthUserSchema.parse(data);
  },
};
